"use client";

import { createNavigation } from "next-intl/navigation";
import config from "@/config.json";
import NextLink from "next/link";
import { useMemo } from "react";

const {
  Link: IntlLink,
  redirect,
  usePathname,
  useRouter,
  getPathname,
} = createNavigation({
  locales: config.websiteLanguages as string[],
  defaultLocale:
    config.defaultWebsiteLanguage ?? (config.websiteLanguages as string[])[0],
  // pathnames: { ... } // при необходимости
});

/**
 * Нормализует href, убирая локаль если она уже есть в пути
 * IntlLink сам добавит правильную локаль
 */
function normalizeHref(href: string | undefined): string | undefined {
  if (!href) return href;

  // Если это внешняя ссылка или уже начинается с локали, возвращаем как есть
  if (
    href.startsWith("http") ||
    href.startsWith("//") ||
    href.startsWith("#")
  ) {
    return href;
  }

  const languages = config.websiteLanguages as string[];
  const pathParts = href.split("/").filter(Boolean);

  // Если первый сегмент - это локаль, убираем её
  if (pathParts.length > 0 && languages.includes(pathParts[0])) {
    pathParts.shift();
    return "/" + pathParts.join("/");
  }

  return href;
}

export const TFLink = ({
  noLang,
  href,
  ...props
}: React.ComponentProps<typeof IntlLink> & { noLang?: boolean }) => {
  // Если noLang=true, используем обычный NextLink без локализации
  if (noLang) {
    // Фильтруем prefetch и popover, если они имеют значения, которые NextLink не поддерживает
    const { prefetch, popover, ...nextLinkProps } = props;
    const safePrefetch =
      String(prefetch) === "unstable_forceStale" ? undefined : prefetch;
    const safePopover = popover === "hint" ? undefined : popover;
    return (
      <NextLink
        href={href}
        {...nextLinkProps}
        prefetch={safePrefetch}
        popover={safePopover}
      />
    );
  }

  // Нормализуем href для IntlLink (убираем локаль если есть, IntlLink сам добавит)
  // Если href это UrlObject, передаем как есть, иначе нормализуем строку
  const normalizedHref = useMemo(() => {
    if (typeof href === "string") {
      return normalizeHref(href) ?? href;
    }
    // UrlObject передаем как есть
    return href;
  }, [href]);

  return <IntlLink href={normalizedHref} {...props} />;
};

export { redirect, usePathname, useRouter, getPathname };
export default TFLink;
